import React, {useRef} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {CallToBook} from 'components/CallToBook/CallToBook'
import {CompactOffer} from 'components/Offer/Compact'
import {OfferTrackingContextProvider} from 'context/offerTracking'
import {OfferViewContext, OfferViewType} from 'context/offerViewContext'
import {useGetHotelDetails} from 'hooks/useGetHotelDetails'
import {
  getAnchorPriceNightlyPerRoom,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {
  getIsCallToBookAvailable,
  getIsNoDatesSearch
} from 'modules/search/selectors'
import {RootState} from 'store'

import {Card} from '@vio/atlas/Card'
import {Box} from '@vio/atlas/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/themes'
import {useNetworkStatus} from '@vio/core/src/_web/utils/network/hooks/useNetworkStatus'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import useIsomorphicLayoutEffect from '@vio/core/src/utils/react/hooks/useIsomorphicLayoutEffect'

import {DatelessCTA, DatelessCTAVariant} from '../DatelessCTA/DatelessCTA'
import {PrimaryRoomOfferCompact} from '../PrimaryRoomOffer'
import {useAccommodationPrimaryOffer} from '../useAccommodationPrimaryOffer'
import {useAccommodationTopOffers} from '../useAccommodationTopOffers'
import {BottomCTASource, getBottomCTASource} from './getBottomCTASource'
import {useTrackDisplayed, useTrackDisplayedProps} from './useTrackDisplayed'

const SafeAreaWrapper = styled.div`
  padding-bottom: env(safe-area-inset-bottom);
`
interface BottomCTABaseCardLayoutProps {
  children: React.ReactNode
  noBorderStyles?: boolean
}

const StyledBottomCtaCard = styled(Card)`
  border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
`

const BottomCTABaseCardLayout: React.FC<BottomCTABaseCardLayoutProps> = ({
  children,
  noBorderStyles
}) => {
  return noBorderStyles ? (
    <SafeAreaWrapper>{children}</SafeAreaWrapper>
  ) : (
    <StyledBottomCtaCard backgroundColorPath="background.neutral.primary">
      <SafeAreaWrapper>{children}</SafeAreaWrapper>
    </StyledBottomCtaCard>
  )
}

const BottomCTATrackDisplayedWrapper: React.FC<
  useTrackDisplayedProps & {
    noBorderStyles?: boolean
  }
> = ({children, noBorderStyles, ...props}) => {
  useTrackDisplayed(props)

  return (
    <BottomCTABaseCardLayout noBorderStyles={noBorderStyles}>
      {children}
    </BottomCTABaseCardLayout>
  )
}

interface BottomCTAProps {
  hotelId: string
  sourceComponent: string
  reportHeight?: (height: number) => void
  noBorderStyles?: boolean
  hideCallToBook?: boolean
}
export const BottomCTA: React.FC<BottomCTAProps> = ({
  hotelId,
  sourceComponent,
  noBorderStyles,
  reportHeight,
  hideCallToBook
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const isHydrated = useIsHydrated()
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)
  const {data: hotel} = useGetHotelDetails(hotelId)

  const {offerEntity, isComplete, searchedParameters} =
    useAccommodationTopOffers({hotelId})
  const anchorPrice = useSelector((state: RootState) =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const {
    anchorPriceTotal,
    offerVariant,
    primaryOffer,
    primaryOfferIndex,
    hasSelectedOfferOrBundle,
    shouldRenderPrimaryRoomOffer
  } = useAccommodationPrimaryOffer({
    offerEntity,
    hotelId
  })
  const {isMobile} = useDeviceLayout()
  const isOnline = useNetworkStatus()
  const isCallToBookAvailable = useSelector(getIsCallToBookAvailable)
  const shouldRenderCallToBook = !hideCallToBook && isCallToBookAvailable
  const {
    showSplitBookingLoader,
    splitBookingOffers,
    hasPrimarySplitBookingOffer
  } = useSelector(state => getSplitBookingDetails(state, hotelId))
  useIsomorphicLayoutEffect(
    () => reportHeight?.(ref.current?.clientHeight || 0),
    [offerEntity, isComplete, reportHeight]
  )

  const source = getBottomCTASource({
    hasDatelessPrice: Boolean(
      isNoDatesSearch && hotel?.pricing?.minRateInUserCurrency
    ),
    isRoomsSourceEnabled: isMobile,
    hasSelectedOfferOrBundle,
    shouldRenderPrimaryRoomOffer,
    isComplete,
    showSplitBookingLoader,
    hasPrimaryOffer: Boolean(primaryOffer),
    hasPrimarySplitBookingOffer
  })

  if (!isHydrated) return null

  if (source === BottomCTASource.Dateless) {
    return (
      <BottomCTABaseCardLayout noBorderStyles={noBorderStyles}>
        <DatelessCTA
          variant={DatelessCTAVariant.BottomCTA}
          priceFrom={hotel.pricing.minRateInUserCurrency}
          hotelId={hotel.objectID}
        />
      </BottomCTABaseCardLayout>
    )
  }

  if (source === BottomCTASource.Rooms) {
    return (
      <div ref={ref}>
        {shouldRenderCallToBook && (
          <CallToBook
            sourceComponent="AccommodationBottomCTA"
            variant="AP_STICKY"
          />
        )}
        <BottomCTABaseCardLayout noBorderStyles={noBorderStyles}>
          <Box padding={noBorderStyles ? 's000' : 's400'}>
            <PrimaryRoomOfferCompact
              hotelId={hotelId}
              component={sourceComponent}
            />
          </Box>
        </BottomCTABaseCardLayout>
      </div>
    )
  }

  if (source === BottomCTASource.Empty) return <div ref={ref} />

  const renderCompactOffer = () => {
    if (source === BottomCTASource.Loading)
      return <CompactOffer variant="CompactDefault" isLoading />
    if (source === BottomCTASource.SplitBooking)
      return (
        <CompactOffer
          isSplitBooking
          splitBookingOffers={splitBookingOffers}
          variant="CompactDefault"
          hotelId={hotelId}
          sourceComponent={sourceComponent}
          hasTag={false}
          disabled={!isOnline}
        />
      )

    return (
      <div data-id="StaticOffer">
        <CompactOffer
          variant="CompactDefault"
          hotelId={hotelId}
          offer={primaryOffer}
          anchorPrice={anchorPrice}
          anchorPriceTotal={anchorPriceTotal}
          offerVariant={offerVariant}
          hasTag={false}
          hasInteraction
          sourceComponent={sourceComponent}
          offerPosition={primaryOfferIndex}
          disabled={!isOnline}
        />
      </div>
    )
  }

  return (
    <div ref={ref}>
      <OfferTrackingContextProvider
        offerEntity={offerEntity}
        parameters={searchedParameters}
      >
        <OfferViewContext.Provider value={OfferViewType.AP_FOOTER}>
          {shouldRenderCallToBook && (
            <CallToBook
              sourceComponent="AccommodationBottomCTA"
              variant="AP_STICKY"
            />
          )}
          <BottomCTATrackDisplayedWrapper
            hotelId={hotelId}
            offer={primaryOffer}
            sourceComponent={sourceComponent}
            offerEntity={offerEntity}
            noBorderStyles={noBorderStyles}
          >
            <Box padding={noBorderStyles ? 's000' : 's400'}>
              {renderCompactOffer()}
            </Box>
          </BottomCTATrackDisplayedWrapper>
        </OfferViewContext.Provider>
      </OfferTrackingContextProvider>
    </div>
  )
}
